import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Activity,
  Users,
  TrendingUp,
  AlertTriangle,
  Bell,
  Shield,
  ShieldCheck,
  Lock,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };
const RISK_COLORS = { low: '#10B981', medium: '#F59E0B', high: '#EF4444', info: '#0D9488' };
const PIE_COLORS = ['#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#CCFBF1'];

const STATIC_DATA = {
  wellness_score: 82,
  active_assessments: 181,
  prediction_accuracy: 95,
  departments: [
    { name: 'Engineering', risk_level: 'low', score: 88, employees: 45 },
    { name: 'Marketing', risk_level: 'medium', score: 65, employees: 32 },
    { name: 'Sales', risk_level: 'high', score: 42, employees: 28 },
    { name: 'HR', risk_level: 'low', score: 91, employees: 15 },
    { name: 'Finance', risk_level: 'medium', score: 71, employees: 22 },
    { name: 'Operations', risk_level: 'low', score: 85, employees: 39 },
  ],
  burnout_drivers: [
    { factor: 'Team Friction', impact: 89 },
    { factor: 'Workplace Fatigue', impact: 76 },
    { factor: 'Workload Stress', impact: 72 },
    { factor: 'Psychological Safety Gap', impact: 58 },
    { factor: 'Role Ambiguity', impact: 45 },
  ],
  alerts: [
    { department: 'Sales', message: 'High attrition risk detected. Retention intervention recommended.', severity: 'high' },
    { department: 'Marketing', message: 'Early workplace fatigue signals. Monitor team friction closely.', severity: 'medium' },
    { department: 'Engineering', message: 'Workforce sentiment improved 12% this quarter.', severity: 'info' },
  ],
};

function FeatureText({ label, title, children }) {
  return (
    <div className="flex flex-col justify-center">
      <p className="text-xs font-semibold tracking-[0.12em] uppercase text-teal-600 mb-3">{label}</p>
      <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-800 tracking-tight mb-4">{title}</h2>
      <div className="text-base text-slate-500 leading-relaxed space-y-4">{children}</div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-xl shadow-slate-300/25 hover:shadow-2xl hover:shadow-teal-500/10 transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-teal-600" />
        </div>
        <span className="text-sm text-slate-500 leading-snug">{label}</span>
      </div>
      <p className="font-heading text-3xl font-semibold text-slate-800" data-testid={testId}>{value}</p>
      {sub && <p className="text-xs text-teal-600 mt-1">{sub}</p>}
    </div>
  );
}

export default function Solutions() {
  const { user } = useAuth();
  const [dashData, setDashData] = useState(null);

  useEffect(() => {
    if (user && user.email) {
      axios
        .get(`${API}/analytics/overview`, { withCredentials: true })
        .then((res) => {
          if (res.data.has_data) {
            const d = res.data;
            setDashData({
              wellness_score: Math.round(d.overall_wellness),
              active_assessments: d.total_employees,
              prediction_accuracy: d.prediction_accuracy,
              departments: d.departments
                .filter((dept) => !dept.privacy_locked)
                .map((dept) => ({
                  name: dept.name,
                  risk_level: dept.risk_level,
                  score: Math.round(dept.wellness_score),
                  employees: dept.respondents,
                })),
              burnout_drivers: d.burnout_drivers.length > 0 ? d.burnout_drivers : undefined,
              alerts: d.departments
                .filter((dept) => !dept.privacy_locked)
                .map((dept) => ({
                  department: dept.name,
                  message:
                    dept.risk_level === 'high'
                      ? `${dept.high_risk_pct}% at high attrition risk. Retention intervention recommended.`
                      : dept.risk_level === 'medium'
                      ? `${dept.high_risk_pct}% showing early burnout signs. Monitor closely.`
                      : `Workforce sentiment at ${Math.round(dept.wellness_score)}%. Looking healthy.`,
                  severity: dept.risk_level === 'high' ? 'high' : dept.risk_level === 'medium' ? 'medium' : 'info',
                })),
            });
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const data = dashData || STATIC_DATA;
  const pieData = data.departments.map((d) => ({ name: d.name, value: d.employees }));

  return (
    <div className="min-h-screen pt-20 bg-[#F8FAFC]" data-testid="solutions-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-[#F8FAFC]">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 60% 30%, #0D9488 0%, transparent 55%)' }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-600/20 border border-teal-500/30 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-teal-300" />
              <span className="text-xs font-medium text-teal-300 tracking-wide">Predictive HR Analytics</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold text-white leading-tight mb-5">
              Predictive People Analytics: Turn Workplace Friction into Team Synergy.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-slate-400 leading-relaxed mb-8">
              Get a live preview of how Udvega&apos;s HR Analytics Dashboard translates raw employee feedback into actionable retention strategies—all while maintaining 100% team-level anonymity.
            </motion.p>
            {!(user && user.email) && (
              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-600/30" data-testid="solutions-demo-btn">
                    Book a Personalized Demo <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="solutions-login-btn">
                    Login to See Live Data
                  </Button>
                </Link>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Interactive Feature Walkthrough */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 space-y-24 md:space-y-32">
          {/* Feature 1 — Predictive Risk Alerts */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
            data-testid="feature-risk-alerts"
          >
            <motion.div variants={fadeUp}>
              <FeatureText label="Feature 01" title="Predictive Risk Alerts for Employee Attrition Prevention">
                <p>
                  Udvega&apos;s People Analytics Dashboard continuously monitors workforce sentiment and flags departments trending toward burnout <em>before</em> resignations spike.
                </p>
                <p>
                  High-attrition-risk teams surface instantly with severity-coded alerts—so HR can intervene with retention playbooks, not exit interviews.
                </p>
                <ul className="space-y-2 text-sm">
                  {['Real-time attrition risk scoring by department', 'Severity-coded alerts for fast HR triage', 'Predictive Retention Accuracy powered by ML-Engine'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </FeatureText>
            </motion.div>

            <motion.div variants={fadeUp} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <KpiCard
                  icon={Activity}
                  label="Workforce Sentiment Index"
                  value={`${data.wellness_score}%`}
                  sub="+5% from last quarter"
                  testId="wellness-score"
                />
                <KpiCard
                  icon={Users}
                  label="Pulse Participation"
                  value={data.active_assessments}
                  sub="Employees engaged"
                  testId="assessments-count"
                />
                <KpiCard
                  icon={TrendingUp}
                  label="Predictive Retention Accuracy (ML-Engine)"
                  value={`${data.prediction_accuracy}%+`}
                  sub="Validated across 300+ teams"
                  testId="accuracy-score"
                />
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-300/25" data-testid="risk-alerts">
                <div className="flex items-center gap-2 mb-5">
                  <Bell className="w-5 h-5 text-slate-600" />
                  <h3 className="font-heading text-lg font-medium text-slate-800">Risk Alerts</h3>
                </div>
                <div className="space-y-3">
                  {data.alerts.map((alert, i) => (
                    <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full" style={{ background: RISK_COLORS[alert.severity] }} />
                        <span className="text-sm font-medium text-slate-700">{alert.department}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">{alert.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Feature 2 — Workplace Culture Friction */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
            data-testid="feature-culture-friction"
          >
            <motion.div variants={fadeUp}>
              <FeatureText label="Feature 02" title="Workplace Culture Friction Intelligence">
                <p>
                  Most culture problems stay invisible until someone quits. Udvega surfaces the friction points that erode psychological safety—Workload Stress, Role Ambiguity, Team Friction—before they become turnover events.
                </p>
                <p>
                  The Top Burnout Drivers chart ranks the highest-impact culture signals across your organization, giving HR a clear priority list for intervention.
                </p>
              </FeatureText>
            </motion.div>

            <motion.div variants={fadeUp} className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-300/25" data-testid="burnout-drivers-chart">
              <h3 className="font-heading text-lg font-medium text-slate-800 mb-6">Top Burnout Drivers</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.burnout_drivers} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: '#94A3B8' }} />
                  <YAxis type="category" dataKey="factor" tick={{ fontSize: 11, fill: '#64748B' }} width={150} />
                  <Tooltip
                    contentStyle={{ background: '#1E293B', border: 'none', borderRadius: '12px', color: '#fff', fontSize: 13 }}
                    labelStyle={{ color: '#94A3B8' }}
                  />
                  <Bar dataKey="impact" fill="#0D9488" radius={[0, 8, 8, 0]} barSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          </motion.div>

          {/* Feature 3 — Department-Level Pulse */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
            data-testid="feature-department-pulse"
          >
            <motion.div variants={fadeUp}>
              <FeatureText label="Feature 03" title="Department-Level Pulse with Anonymity Firewall">
                <p>
                  Every department gets its own health score, risk level, and respondent count—giving HR a granular People Analytics Dashboard without ever exposing an individual.
                </p>
                <p>
                  Udvega&apos;s built-in Anonymity Firewall automatically suppresses analytics for teams with fewer than 2 responses, preventing contextual identity guessing.
                </p>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-teal-50 border border-teal-100 mt-2">
                  <ShieldCheck className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-teal-800">
                    <strong>Anonymity Safeguard:</strong> Teams below the 2-response threshold show as encrypted &amp; hidden—never guessable, never traceable.
                  </p>
                </div>
              </FeatureText>
            </motion.div>

            <motion.div variants={fadeUp} className="space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-300/25" data-testid="department-heatmap">
                <div className="flex items-center gap-2 mb-5">
                  <Shield className="w-5 h-5 text-slate-600" />
                  <h3 className="font-heading text-lg font-medium text-slate-800">Department Health Overview</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {data.departments.map((dept) => (
                    <div
                      key={dept.name}
                      className="p-4 rounded-2xl border transition-all hover:shadow-md"
                      style={{
                        borderColor: RISK_COLORS[dept.risk_level] + '40',
                        background: RISK_COLORS[dept.risk_level] + '08',
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">{dept.name}</span>
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: RISK_COLORS[dept.risk_level] }} />
                      </div>
                      <p className="font-heading text-2xl font-semibold text-slate-800">{dept.score}%</p>
                      <p className="text-xs text-slate-400">{dept.employees} respondents</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-300/25" data-testid="team-distribution">
                <h3 className="font-heading text-lg font-medium text-slate-800 mb-4">Team Distribution</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke="none">
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#1E293B', border: 'none', borderRadius: '12px', color: '#fff', fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 mt-2">
                  {pieData.map((d, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-xs text-slate-500">{d.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Notice */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="p-5 rounded-2xl bg-teal-50 border border-teal-100 flex items-start gap-3"
            data-testid="anonymity-notice"
          >
            <Lock className="w-5 h-5 text-teal-600 mt-0.5 shrink-0" />
            <p className="text-sm text-teal-800">
              <strong>Privacy Notice:</strong> All data displayed is aggregated at the team level. Individual employee responses are encrypted and never visible to management. Analytics only unlock when a department has 2+ respondents.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="py-20 md:py-24 relative overflow-hidden" data-testid="solutions-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-700 to-slate-900" />
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 80% 50%, #2DD4BF 0%, transparent 50%)' }} />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
              Stop guessing. Start retaining your top talent.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
              Deploy Udvega&apos;s predictive culture intelligence in less than 10 minutes.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-teal-700 hover:bg-teal-50 px-10 h-14 text-base font-semibold shadow-xl shadow-teal-900/30 hover:shadow-2xl hover:shadow-teal-900/40 transition-shadow"
                  data-testid="solutions-cta-btn"
                >
                  Book a Personalized Demo <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
